import { useState, useEffect } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, XMarkIcon, PlusIcon, ShareIcon } from '@heroicons/react/24/outline';
import { userSession } from '../utils/userSession.js';
import { trackEvent, EventTypes } from '../utils/analytics.js';
import { generateEventImage } from '../utils/imageGenerator.js';
import UserApiKeys from './UserApiKeys.js';

export default function UserDashboardHome({ savedEvents, isLoading, error, onEventsChange }) {
  const [partnerOptIn, setPartnerOptIn] = useState(false);

  // Load partner opt-in status from user session
  useEffect(() => {
    const user = userSession.getUser();
    if (user && user.partnerOptIn !== undefined) {
      setPartnerOptIn(user.partnerOptIn);
    }
  }, []);

  const handlePartnerOptInToggle = async (newValue) => {
    try {
      const userId = userSession.getUserId();
      if (!userId) {
        console.error('User ID not found');
        return;
      }

      const response = await fetch(
        `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/user/${userId}/partnerOptIn`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ partnerOptIn: newValue }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update partner opt-in preference');
      }

      const result = await response.json();
      
      // Update local state
      setPartnerOptIn(newValue);
      
      // Update user session
      const user = userSession.getUser();
      if (user) {
        userSession.setUser({
          ...user,
          partnerOptIn: newValue
        });
      }

      console.log('Partner opt-in updated successfully:', result);
    } catch (error) {
      console.error('Error updating partner opt-in:', error);
      // Revert the toggle on error
      setPartnerOptIn(!newValue);
    }
  };
  const removeEvent = async (eventId) => {
    try {
      const userId = userSession.getUserId();
      const response = await fetch(
        `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/userevents/${userId}?eventId=${eventId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to remove event');
      }

      if (onEventsChange) {
        onEventsChange();
      }
    } catch (err) {
      console.error('Error removing event:', err);
    }
  };

  const getComingEventsCount = () => {
    if (!savedEvents) return 0;
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    return savedEvents.filter(event => {
      const eventDate = new Date(event.StartDateTime);
      return eventDate <= thirtyDaysFromNow && eventDate >= new Date();
    }).length;
  };

  const groupEventsByMonth = () => {
    if (!savedEvents) return {};
    
    const currentDate = new Date();
    return savedEvents.reduce((groups, event) => {
      const eventDate = new Date(event.StartDateTime);
      if (eventDate >= currentDate) {  // Only include future events
        const monthKey = eventDate.toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!groups[monthKey]) {
          groups[monthKey] = [];
        }
        groups[monthKey].push(event);
      }
      return groups;
    }, {});
  };

  const renderInfoBoxes = () => (
    <div className="grid grid-cols-2 gap-4 mb-6">
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg p-4 text-center">
        <div className="text-4xl font-bold text-white ">
          {savedEvents?.length || 0}
        </div>
        <div className="text-xs text-white">
          saved events
        </div>
      </div>
      <div className="bg-gradient-to-br from-slate-800 to-slate-600 rounded-lg p-4 text-center">
        <div className="text-4xl font-bold text-white">
          {getComingEventsCount()}
        </div>
        <div className="text-xs text-white">
          events in the next 30 days
        </div>
      </div>
    </div>
  );

  const formatTimeRange = (startDateTime, endDateTime) => {
    const start = startDateTime.split('T')[1].substring(0, 5);
    const end = endDateTime.split('T')[1].substring(0, 5);
    
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    };

    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC'
    });
  };

  const handleEventClick = (event) => {
    trackEvent(EventTypes.EVENT_CLICK, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName,
      view_type: 'dashboard',
      click_target: 'title'
    });
  };

  const handleShareAllEvents = async () => {
    try {
      const image = await generateEventImage('All Saved Events', savedEvents);
      // Create a temporary link to download the image
      const link = document.createElement('a');
      link.href = image;
      link.download = `driftcal-all-saved-events.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      trackEvent(EventTypes.SHARE_MONTH, {
        month: 'All Saved Events',
        event_count: savedEvents.length
      });
    } catch (error) {
      console.error('Error sharing all events:', error);
    }
  };

  const renderEventsByMonth = () => {
    const groupedEvents = groupEventsByMonth();
    const months = Object.keys(groupedEvents).sort((a, b) => {
      const dateA = new Date(Date.UTC(new Date(a).getFullYear(), new Date(a).getMonth()));
      const dateB = new Date(Date.UTC(new Date(b).getFullYear(), new Date(b).getMonth()));
      return dateA - dateB;
    });

    return months.map(month => (
      <div key={month} className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-slate-900">
            {month}
          </h3>
        </div>
        <div className="space-y-3">
          {groupedEvents[month].map(event => (
            <div key={event.EventID} className="bg-white border rounded-lg p-4 shadow-sm">
              <h4 className="font-semibold text-slate-900">
                <a 
                  href={event.EventLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => handleEventClick(event)}
                >
                  {event.EventType === 'Competition' && event.RoundNum
                    ? `${event.EventName} - Round ${event.RoundNum}`
                    : event.EventName}
                </a>
              </h4>
              {event.EventType && (
                <p className="text-xs text-slate-500 mt-1">
                  {`${event.BusinessName} - ${event.EventType}`}
                </p>
              )}
              <div className="flex items-center mt-1 text-sm text-slate-600">
                <CalendarIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                <span>
                  {formatDate(event.StartDateTime)}
                </span>
              </div>
              <div className="flex items-center mt-1 text-sm text-slate-600">
                <ClockIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                <span>
                  {formatTimeRange(event.StartDateTime, event.EndDateTime)}
                </span>
              </div>
              <div className="flex items-center mt-1 text-sm text-slate-600">
                <MapPinIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.VenueName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600"
                >
                  {event.VenueName}
                </a>
              </div>
              <div className="flex items-center justify-end mt-2 pt-2 border-t">
                <button
                  onClick={() => removeEvent(event.EventID)}
                  className="flex items-center text-xs text-red-600 hover:text-red-800"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div>
      <UserApiKeys />
      {isLoading ? (
        <div>Loading...</div>
      ) : error ? (
        <div>{error}</div>
      ) : savedEvents.length === 0 ? (
        <div>
          {/* Partner Opt-In Toggle */}
          <div className="bg-white border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="partnerOptIn" className="block text-sm font-medium text-gray-900 mb-1">
                  Receive Partner Offers
                </label>
                <p className="text-xs text-gray-500">
                  Keep DriftCal alive! Opt-in to receive special offers from our partners.
                </p>
              </div>
              <button
                id="partnerOptIn"
                type="button"
                onClick={() => handlePartnerOptInToggle(!partnerOptIn)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                  partnerOptIn ? 'bg-slate-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={partnerOptIn}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    partnerOptIn ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
          
          <div className="text-center py-12">
            <div className="bg-white border rounded-lg p-8 shadow-sm max-w-md mx-auto">
              <PlusIcon className="h-12 w-12 text-slate-900 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Your DriftCal is Empty
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Start saving events to your calendar by clicking the + icon on any event you're interested in.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {renderInfoBoxes()}
          
          {/* Partner Opt-In Toggle */}
          <div className="bg-white border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="partnerOptIn" className="block text-sm font-medium text-gray-900 mb-1">
                  Receive Partner Offers
                </label>
                <p className="text-xs text-gray-500">
                  Keep DriftCal alive! Opt-in to receive special offers from our partners.
                </p>
              </div>
              <button
                id="partnerOptIn"
                type="button"
                onClick={() => handlePartnerOptInToggle(!partnerOptIn)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                  partnerOptIn ? 'bg-slate-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={partnerOptIn}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    partnerOptIn ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
          
          <div className="flex justify-center mb-6">
            <button
              onClick={handleShareAllEvents}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
            >
              <ShareIcon className="h-5 w-5 mr-2" />
              Share All Saved Events
            </button>
          </div>
          {renderEventsByMonth()}
        </div>
      )}
    </div>
  );
} 